import {t, Trans} from '@lingui/macro'
import {Formik} from 'formik'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'

import {
    Button,
    Inline,
    FormikInput as Input,
    Loading,
    LoadingPage,
    Stack,
    Text,
    tokens,
    Tooltip,
} from '@pleo-io/telescope'
import {Info} from '@pleo-io/telescope-icons'

import {ContactSupport} from '~/feature-ui/contact-support'
import Address from '~/generic-ui/address/address'
import {useActionWithFeedback} from '~/generic-ui/use-action-with-feedback/use-action-with-feedback'
import {useToaster} from '~/shell/toaster'
import yup from '~/validation/yup'

import {GenericError} from '../../../components/generic-error'
import {SplitContent} from '../../../components/split-content'
import {usePartnerClientSession} from '../../../lib/use-partner-client-session'
import {bff} from '../../../trpc-client'
import {PartnerTierCard} from '../components/partner-tier-card'

type FormValues = {tradingName: string}

const validationSchema = yup.object().shape({
    tradingName: yup.string().required(() => t`Please enter the trading name`),
})

export const CompanyInfo = () => {
    const {showToast} = useToaster()
    const {switchClient} = usePartnerClientSession()
    const navigate = useNavigate()
    const {data: companyInfoData, isError: companyInfoError} =
        bff.companyInfo.getCompanyInfo.useQuery()

    const {mutateAsync: updatePartnerInfo} = bff.companyInfo.updateCompanyInfo.useMutation({
        onSuccess() {
            showToast(t`Trading name updated successfully.`, {level: 'success'})
        },
        onError() {
            showToast(t`Could not change the trading name. Please try again or contact support.`, {
                level: 'error',
            })
        },
    })

    const {mutateAsync: createOwnPleoCompany, isLoading: isCreatingOwnPleoCompany} =
        bff.companyInfo.createOwnPleoCompany.useMutation()

    const pleoOwnCompany = companyInfoData?.ownCompany

    const handleOnboardOwnCompany = useActionWithFeedback(
        async () => {
            const {ownCompanyId} = await createOwnPleoCompany()
            await switchClient(ownCompanyId)
            navigate('/company-verification')
        },
        () => ({
            confirmTitle: t`Create company account`,
            confirmDescription: t`Do you want to start using Pleo for your own business?`,
            confirmLabel: t`Create account`,
            successMessage: t`Your own company created, redirecting...`,
            errorMessage: t`An error occurred. Please try again later or contact support.`,
        }),
    )

    if (companyInfoError) {
        return <GenericError />
    }

    if (!companyInfoData) {
        return <LoadingPage />
    }

    return (
        <Stack mt={32} stretch>
            <SplitContent reverse>
                <SplitContent.Main>
                    <CompaniesGrid>
                        <div>
                            <Text variant="large-accent" weight="semibold" as="h2" space={10}>
                                <Trans>Practice Info</Trans>
                            </Text>
                            <Text variant="small-subtle">
                                <Trans>Legal name</Trans>
                            </Text>
                            <Text variant="large-accent" space={20}>
                                {companyInfoData.name}
                            </Text>
                            <div>
                                <Text variant="small-subtle">
                                    <Trans>Address</Trans>
                                </Text>
                                <Text variant="large-accent" space={20}>
                                    <Address
                                        address={{
                                            addressLine1:
                                                companyInfoData.address?.addressLine1 || '',
                                            addressLine2: companyInfoData.address?.addressLine2,
                                            country: companyInfoData.address?.country || '',
                                            locality: companyInfoData.address?.locality || '',
                                            postalCode: '',
                                        }}
                                    />
                                </Text>
                            </div>
                            <div>
                                <Text variant="small-subtle">
                                    <Trans>Registration no.</Trans>
                                </Text>
                                <Text variant="large-accent" space={20}>
                                    {companyInfoData.registrationNumber}
                                </Text>
                            </div>
                            <Text as="p" space={20}>
                                <ContactSupport
                                    chatLabel={t`Get in touch if something has changed`}
                                    emailLabel={t`Drop us an email if something has changed`}
                                    emailSubject={t`(${companyInfoData.name})'s company information has changed.`}
                                />
                            </Text>
                        </div>
                        <div>
                            <Text variant="large-accent" weight="semibold" as="h2" space={10}>
                                <Trans>Own Pleo Company</Trans>
                            </Text>
                            {!pleoOwnCompany && (
                                <Text as="p">
                                    <Trans>Start using Pleo for your own business.</Trans>{' '}
                                    {isCreatingOwnPleoCompany ? (
                                        <Loading size={8} />
                                    ) : (
                                        <Button variant="link" onClick={handleOnboardOwnCompany}>
                                            <Trans>Create account now</Trans> →
                                        </Button>
                                    )}
                                </Text>
                            )}
                            {pleoOwnCompany && (
                                <>
                                    <Text variant="small-subtle">
                                        <Trans>Legal name</Trans>
                                    </Text>
                                    <Text variant="large-accent" space={20}>
                                        {pleoOwnCompany.name}
                                    </Text>

                                    {pleoOwnCompany.address && (
                                        <div>
                                            <Text variant="small-subtle">
                                                <Trans>Address</Trans>
                                            </Text>
                                            <Text variant="large-accent" space={20}>
                                                <Address
                                                    address={{
                                                        addressLine1:
                                                            pleoOwnCompany.address.addressLine1 ||
                                                            '',
                                                        addressLine2:
                                                            pleoOwnCompany.address.addressLine2 ||
                                                            '',
                                                        country:
                                                            pleoOwnCompany.address.country || '',
                                                        locality:
                                                            pleoOwnCompany.address.locality || '',
                                                        postalCode: '',
                                                    }}
                                                />
                                            </Text>
                                        </div>
                                    )}
                                    <div>
                                        <Text variant="small-subtle">
                                            <Trans>Registration number</Trans>
                                        </Text>
                                        <Text variant="large-accent" space={20}>
                                            {pleoOwnCompany.registrationNumber}
                                        </Text>
                                    </div>
                                </>
                            )}
                        </div>
                    </CompaniesGrid>
                    <Stack as="section" mb={24} mt={24}>
                        <Inline alignItems="center" mb={10}>
                            <Text variant="large-accent" weight="semibold" as="h2">
                                <Trans>Trading Name</Trans>
                            </Text>
                            <Tooltip
                                content={t`The trading name will be used for communication with your clients.`}
                            >
                                <span>
                                    <Info ml={4} mb={4} size={16} />
                                </span>
                            </Tooltip>
                        </Inline>
                        {companyInfoData.isPartnerOwner ? (
                            <Formik<FormValues>
                                initialValues={{tradingName: companyInfoData.tradingName || ''}}
                                validationSchema={validationSchema}
                                onSubmit={async (values, formik) => {
                                    await updatePartnerInfo(values)
                                    formik.resetForm({values})
                                }}
                            >
                                {(formik) => (
                                    <Stack as="form" onSubmit={formik.handleSubmit} stretch>
                                        <Input
                                            placeholder={t`Trading Name`}
                                            aria-label={t`Trading Name`}
                                            name="tradingName"
                                            variant="naked"
                                        />
                                        <Stack mt={24}>
                                            <Inline space={16}>
                                                <Button
                                                    variant="primary"
                                                    type="submit"
                                                    disabled={!formik.dirty || !formik.isValid}
                                                    loading={formik.isSubmitting}
                                                >
                                                    <Trans>Save</Trans>
                                                </Button>
                                                {formik.dirty && !formik.isSubmitting && (
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        onClick={() => formik.resetForm()}
                                                    >
                                                        <Trans>Cancel</Trans>
                                                    </Button>
                                                )}
                                            </Inline>
                                        </Stack>
                                    </Stack>
                                )}
                            </Formik>
                        ) : (
                            <Text variant="large-accent" space={20}>
                                {companyInfoData.tradingName}
                            </Text>
                        )}
                    </Stack>
                </SplitContent.Main>
                <SplitContent.Right>
                    {companyInfoData.partnerProgramEnabled && (
                        <SplitContent.Sticky>
                            <Stack space={32} stretch>
                                <PartnerTierCard />
                            </Stack>
                        </SplitContent.Sticky>
                    )}
                </SplitContent.Right>
            </SplitContent>
        </Stack>
    )
}

const CompaniesGrid = styled.div`
    display: grid;
    gap: ${tokens.spacing16};
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
`
